import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ALL_TIME_END, ALL_TIME_NAME, ALL_TIME_START, RULE_MODEL } from '../Model/Model';
import {GetPayrollPolicy, UpdatePayrollPolicy} from "../../../../../../../../reduxToolkit/PayrollPolicy/PayrollPolicyApi";
import {errorMsg, validateDecimal} from "../../../../../../../../Apis/functions";
import {START_TIME} from "../../../../../Employees/Model/Models";
import HandleChange from "../../../../../../../../Helpers/HandleChange";
import routes from "../../../../../../../../Routes/routes";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

const UpdatePolicyApp = () => {
    const location = useLocation();
    const state = location.state;
    const id = state?.id ?? null;
    const isGlobal = state?.isGlobal ?? true;

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [item, setItem] = useState({ ...RULE_MODEL, isGlobal: isGlobal });
    const [emptyFields, setEmptyFields] = useState(false);
    const [isValid, setIsValid] = useState(true);
    const [isValidCalculationValue, setIsValidCalculationValue] = useState(true);
    const [isValidCalculationValueText, setIsValidCalculationValueText] = useState('this_value_must_be_greater_than_0');
    const { handleChange: baseHandleChange } = HandleChange({ model: item, setModel: setItem });

    /**
     * Re-validates scope and calculation value whenever the item model changes.
     */
    useEffect(() => {
        setIsValid(item.isValidScope());
        const { isValid: calcValid, text: calcText } = validateDecimal(item.calculationValue);
        setIsValidCalculationValue(calcValid);
        if (calcText) setIsValidCalculationValueText(calcText);
    }, [item]);

    /**
     * Loads the existing policy on mount using the id from navigation state.
     */
    useEffect(() => {
        if (id) getPolicyById(id);
    }, []);

    /**
     * Handles field changes, applying special logic for allTime and condition fields.
     * @param {React.ChangeEvent} e - The change event from the input.
     */
    const handleChange = (e) => {
        const { name: field, value } = e.target;
        if (field === ALL_TIME_NAME) {
            setItem((prev) => ({
                ...prev,
                [field]: value,
                startTime: ALL_TIME_START,
                endTime: value ? ALL_TIME_END : START_TIME,
            }));
            return;
        }
        if (field === 'condition') {
            setItem(item.changeScope(value));
            return;
        }
        baseHandleChange(e);
    };

    const updateRuleField = (field, value) => handleChange({ target: { name: field, value } });

    /**
     * Fetches a single policy by ID and hydrates the item model.
     * @param {string} policyId - UUID of the policy to retrieve.
     */
    const getPolicyById = (policyId) => {
        dispatch(GetPayrollPolicy(policyId)).then(({ payload: { data: { data, code, success } } }) => {
            if (success === true) {
                setItem(item.set(data));
            } else {
                errorMsg(code);
            }
        });
    };

    /**
     * Validates the form and dispatches the update request. Shows a warning toast if validation fails.
     */
    const handleUpdate = () => {
        if (!item.isValid(false) || !isValid || !isValidCalculationValue) {
            setEmptyFields(true);
            toast.warning(t('please_fill_all_fields'));
            return;
        }

        dispatch(UpdatePayrollPolicy(RULE_MODEL.toRegister(item))).then(
            ({ payload: { data: { success, code } } }) => {
                if (success === true) {
                    toast.success(t('has_been_updated_successfully'));
                    getPolicyById(id);
                } else {
                    errorMsg(code);
                }
            }
        );
    };

    /**
     * Navigates back to the previous screen using the backUrl from navigation state.
     */
    const goBack = () => {
        if (state?.backUrl) {
            navigate(state.backUrl, { state: { ...state } });
        } else {
            navigate(routes.attendance.main);
        }
    };

    return {
        t,
        item,
        setItem,
        isGlobal,
        emptyFields,
        isValid,
        isValidCalculationValue,
        isValidCalculationValueText,
        handleUpdate,
        goBack,
        handleChange,
        updateRuleField
    };
};

export default UpdatePolicyApp;
